import { AvailableOption, TestCategory, TestConfiguration, TestDefinition } from "types/testTypes";
import { MessageOpcode } from "types/MessageOpcode";
import * as TestSpecifics from "@testSpecificConstants/index";
import { CATEGORY_VERSION_MAP } from "./versionConstants";
import { availablVFATestProgressConfigurationOptions } from "@testSpecificConstants/index";
import { ValidPath } from "types/devTypes";
import {
  availableDyscalculiaTestConfigurationOptions,
  dyscalculiaTestConfiguration,
  dyscalculiaUIConfiguration,
} from "./testSpecificConstants/dyscalculiaConstants";
import {
  availableWCSTTestConfigurationOptions,
  wcstTestConfiguration,
  wcstUIConfiguration,
} from "./testSpecificConstants/wcstConstants";
import {
  availableNPCTTestConfigurationOptions,
  npctTestConfiguration,
  npctUIConfiguration,
} from "./testSpecificConstants/npctConstants";

/**
 * List of configuration fields that should be persisted between tests.
 * You can add any field or nested field that belongs to TestConfiguration interface here
 * Upon choosing a new test configuration, these fields will be merged back into the new configuration
 */
export const PERSISTED_CONFIG_FIELD_PATHS = [
  "virtualAssistanceConfig.showAssistant",
  "virtualAssistanceConfig.quickAssistant",
  "virtualAssistanceConfig.assistantLanguage",
  "voiceAssistantConfig.isVoiceAssistantEnabled",
  "voiceAssistantConfig.assistantLanguage",
] as const satisfies readonly ValidPath<TestConfiguration>[];

export const SAVED_TEST_FIELD_MAP: Record<TestCategory, ValidPath<TestConfiguration>[]> = {
  [TestCategory.VisualField]: [
    "extraData.testBehaviour",
    "extraData.fixationSize",
    "extraData.stimuliSize",
    "testSpeed",
    "extraData.fovealThresholdTestingEnabled",
    "eyeConfiguration.autoPauseForGaze",
    "eyeConfiguration.isTwoScreenFixationEnabled",
    "eyeConfiguration.isCalibrationNeeded",
    "testType",
  ],
  [TestCategory.FDT]: ["testType", "eyeConfiguration.autoPauseForGaze"],
  [TestCategory.ContrastSensitivity]: [],
  [TestCategory.VisualAcuity]: [],
  [TestCategory.ColorVision]: [],
  [TestCategory.AmslerGrid]: [],
  [TestCategory.Pupillometry]: [],
  [TestCategory.Dyslexia]: [],
  [TestCategory.ReadingAssessment]: [],
  [TestCategory.ContinuousPerformance]: [],
  [TestCategory.OcularMotility]: [],
  [TestCategory.Cover]: [],
  [TestCategory.StereoAcuity]: [],
  [TestCategory.Videonystagmography]: [],
  [TestCategory.Kinetic]: [],
  [TestCategory.Dyscalculia]: [],
  [TestCategory.WisconsinCardSorting]: [],
  [TestCategory.BinocularVision]: [],
  [TestCategory.OptokineticNystagmus]: [],
};

export const ALL_TEST_CATEGORIES: AvailableOption<TestCategory> = {
  configurationFieldName: "name",
  choices: [
    {
      value: TestCategory.VisualField,
      validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.VisualField],
    },
    { value: TestCategory.FDT, validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.FDT] },
    {
      value: TestCategory.ContrastSensitivity,
      validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.ContrastSensitivity],
    },
    {
      value: TestCategory.VisualAcuity,
      validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.VisualAcuity],
    },
    {
      value: TestCategory.ColorVision,
      validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.ColorVision],
    },
    {
      value: TestCategory.AmslerGrid,
      validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.AmslerGrid],
    },
    {
      value: TestCategory.Pupillometry,
      validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.Pupillometry],
    },
    {
      value: TestCategory.Dyslexia,
      validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.Dyslexia],
    },
    {
      value: TestCategory.ReadingAssessment,
      validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.ReadingAssessment],
    },
    {
      value: TestCategory.ContinuousPerformance,
      validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.ContinuousPerformance],
    },
    {
      value: TestCategory.OcularMotility,
      validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.OcularMotility],
    },
    { value: TestCategory.Cover, validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.Cover] },
    {
      value: TestCategory.StereoAcuity,
      validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.StereoAcuity],
    },
    {
      value: TestCategory.Videonystagmography,
      validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.Videonystagmography],
    },
    { value: TestCategory.Kinetic, validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.Kinetic] },
    {
      value: TestCategory.Dyscalculia,
      validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.Dyscalculia],
    },
    {
      value: TestCategory.WisconsinCardSorting,
      validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.WisconsinCardSorting],
    },
    {
      value: TestCategory.BinocularVision,
      validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.BinocularVision],
    },
    {
      value: TestCategory.OptokineticNystagmus,
      validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.OptokineticNystagmus],
    },
  ],
  validSinceVersion: null,
  isMandatory: true,
};

export const TEST_DEFINITIONS: Record<TestCategory, TestDefinition> = {
  [TestCategory.VisualField]: {
    availableOptions: TestSpecifics.availableVFATestConfigurationOptions,
    defaultConfiguration: TestSpecifics.defaultVFATestConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.STATUS_START,
      receivedMessage: MessageOpcode.UPDATE_STATUS,
    },
    availableProgressConfigurations: availablVFATestProgressConfigurationOptions,
    defaultProgressConfiguration: TestSpecifics.defaultVFAProgressConfiguration,
    uiConfiguration: TestSpecifics.defaultVFAUIConfiguration,
  },
  [TestCategory.FDT]: {
    availableOptions: TestSpecifics.availableFDTTestConfigurationOptions,
    defaultConfiguration: TestSpecifics.fdtTestConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.FDT_TEST_START,
      receivedMessage: MessageOpcode.FDT_TEST_UPDATE,
    },
    availableProgressConfigurations: TestSpecifics.availableFDTTestProgressConfigurationOptions,
    defaultProgressConfiguration: TestSpecifics.defaultFDTProgressConfiguration,
    uiConfiguration: TestSpecifics.fdtUIConfiguration,
  },
  [TestCategory.ContrastSensitivity]: {
    availableOptions: TestSpecifics.availableContrastSensitivityTestConfigurationOptions,
    defaultConfiguration: TestSpecifics.contrastSensitivityTestConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.CONTRAST_TEST_START,
      receivedMessage: MessageOpcode.CONTRAST_TEST_UPDATE,
      nextMessage: MessageOpcode.CONTRAST_TEST_NEXT,
    },
    availableProgressConfigurations: null,
    defaultProgressConfiguration: null,
    uiConfiguration: TestSpecifics.contrastSensitivityUIConfiguration,
  },
  [TestCategory.ColorVision]: {
    availableOptions: TestSpecifics.availableColorVisionTestConfigurationOptions,
    defaultConfiguration: TestSpecifics.colorVisionTestConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.COLOR_TEST_START,
      receivedMessage: MessageOpcode.COLOR_TEST_UPDATE,
      nextMessage: MessageOpcode.COLOR_TEST_NEXT,
    },
    availableProgressConfigurations: null,
    defaultProgressConfiguration: null,
    uiConfiguration: TestSpecifics.colorVisionUIConfiguration,
  },
  [TestCategory.VisualAcuity]: {
    availableOptions: TestSpecifics.availableVisualAcuityTestConfigurationOptions,
    defaultConfiguration: TestSpecifics.visualAcuityTestConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.VA_TEST_START,
      receivedMessage: MessageOpcode.VA_TEST_UPDATE,
      nextMessage: MessageOpcode.VA_TEST_NEXT,
    },
    availableProgressConfigurations: null,
    defaultProgressConfiguration: null,
    uiConfiguration: TestSpecifics.visualAcuityUIConfiguration,
  },
  [TestCategory.AmslerGrid]: {
    availableOptions: TestSpecifics.availableAmslerGridTestConfigurationOptions,
    defaultConfiguration: TestSpecifics.amslerGridTestConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.AMSLER_GRID_START,
      receivedMessage: MessageOpcode.AMSLER_GRID_UPDATE,
      nextMessage: MessageOpcode.AMSLER_GRID_NEXT,
    },
    availableProgressConfigurations: null,
    defaultProgressConfiguration: null,
    uiConfiguration: TestSpecifics.amslerGridUIConfiguration,
  },
  [TestCategory.Pupillometry]: {
    availableOptions: TestSpecifics.availablePupillometryTestConfigurationOptions,
    defaultConfiguration: TestSpecifics.pupillometryTestConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.PUPILLOMETRY_TEST_START,
      receivedMessage: MessageOpcode.PUPILLOMETRY_TEST_UPDATE,
    },
    availableProgressConfigurations: null,
    defaultProgressConfiguration: null,
    uiConfiguration: TestSpecifics.pupillometryUIConfiguration,
  },
  [TestCategory.Dyslexia]: {
    availableOptions: TestSpecifics.availableDyslexiaTestConfigurationOptions,
    defaultConfiguration: TestSpecifics.dyslexiaTestConfiguration,
    uiConfiguration: TestSpecifics.dyslexiaUIConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.DYSLEXIA_TEST_START,
      receivedMessage: MessageOpcode.DYSLEXIA_TEST_UPDATE,
      nextMessage: MessageOpcode.DYSLEXIA_TEST_NEXT,
    },
    availableProgressConfigurations: null,
    defaultProgressConfiguration: null,
  },
  [TestCategory.ReadingAssessment]: {
    availableOptions: TestSpecifics.availableReadingAssessmentTestConfigurationOptions,
    defaultConfiguration: TestSpecifics.readingAssessmentTestConfiguration,
    uiConfiguration: TestSpecifics.readingAssessmentUIConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.READING_ASSESSMENT_TEST_START,
      receivedMessage: MessageOpcode.READING_ASSESSMENT_TEST_UPDATE,
      nextMessage: MessageOpcode.READING_ASSESSMENT_TEST_NEXT,
    },
    availableProgressConfigurations: null,
    defaultProgressConfiguration: null,
  },
  [TestCategory.ContinuousPerformance]: {
    availableOptions: TestSpecifics.availableCPTConfigurationOptions,
    defaultConfiguration: TestSpecifics.cptConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.CONTINUOUS_PERFORMANCE_TEST_START,
      receivedMessage: MessageOpcode.CONTINUOUS_PERFORMANCE_UPDATE,
    },
    availableProgressConfigurations: null,
    defaultProgressConfiguration: null,
    uiConfiguration: TestSpecifics.cptUIConfiguration,
  },
  [TestCategory.OcularMotility]: {
    availableOptions: TestSpecifics.availableOcularMotilityTestConfigurationOptions,
    defaultConfiguration: TestSpecifics.ocularMotilityTestConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.OCULAR_MOTILITY_TEST_START,
      receivedMessage: MessageOpcode.OCULAR_MOTILITY_TEST_UPDATE,
    },
    availableProgressConfigurations: null,
    defaultProgressConfiguration: null,
    uiConfiguration: TestSpecifics.ocularMotilityUIConfiguration,
  },
  [TestCategory.Cover]: {
    availableOptions: TestSpecifics.availableCoverTestConfigurationOptions,
    defaultConfiguration: TestSpecifics.coverTestConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.COVER_TEST_START,
      receivedMessage: MessageOpcode.COVER_TEST_UPDATE,
    },
    availableProgressConfigurations: null,
    defaultProgressConfiguration: null,
    uiConfiguration: TestSpecifics.coverUIConfiguration,
  },
  [TestCategory.StereoAcuity]: {
    availableOptions: TestSpecifics.availableStereoAcuityTestConfigurationOptions,
    defaultConfiguration: TestSpecifics.stereoAcuityTestConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.STEREO_ACUITY_TEST_START,
      receivedMessage: MessageOpcode.STEREO_ACUITY_TEST_UPDATE,
      nextMessage: MessageOpcode.STEREO_ACUITY_TEST_NEXT,
    },
    availableProgressConfigurations: null,
    defaultProgressConfiguration: null,
    uiConfiguration: TestSpecifics.stereoAcuityUIConfiguration,
  },
  [TestCategory.Videonystagmography]: {
    availableOptions: TestSpecifics.availableVideonystagmographyTestConfigurationOptions,
    defaultConfiguration: TestSpecifics.videonystagmographyTestConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.VIDEONYSTAGMOGRAPHY_TEST_START,
      receivedMessage: MessageOpcode.VIDEONYSTAGMOGRAPHY_TEST_UPDATE,
      nextMessage: MessageOpcode.VIDEONYSTAGMOGRAPHY_TEST_NEXT,
    },
    availableProgressConfigurations: null,
    defaultProgressConfiguration: null,
    uiConfiguration: TestSpecifics.videonystagmographyUIConfiguration,
  },
  [TestCategory.Kinetic]: {
    availableOptions: TestSpecifics.availableKineticPerimetryTestConfigurationOptions,
    defaultConfiguration: TestSpecifics.kineticPerimetryTestConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.KINETIC_TEST_START,
      receivedMessage: MessageOpcode.KINETIC_TEST_UPDATE,
      nextMessage: MessageOpcode.KINETIC_TEST_NEXT,
    },
    availableProgressConfigurations: null,
    defaultProgressConfiguration: null,
    uiConfiguration: TestSpecifics.kineticPerimetryUIConfiguration,
  },
  [TestCategory.Dyscalculia]: {
    availableOptions: availableDyscalculiaTestConfigurationOptions,
    defaultConfiguration: dyscalculiaTestConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.DYSCALCULIA_TEST_START,
      receivedMessage: MessageOpcode.DYSCALCULIA_TEST_UPDATE,
      nextMessage: MessageOpcode.DYSCALCULIA_TEST_NEXT,
    },
    availableProgressConfigurations: null,
    defaultProgressConfiguration: null,
    uiConfiguration: dyscalculiaUIConfiguration,
  },
  [TestCategory.WisconsinCardSorting]: {
    availableOptions: availableWCSTTestConfigurationOptions,
    defaultConfiguration: wcstTestConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.WCST_TEST_START,
      receivedMessage: MessageOpcode.WCST_TEST_UPDATE,
      nextMessage: MessageOpcode.WCST_TEST_NEXT,
    },
    availableProgressConfigurations: null,
    defaultProgressConfiguration: null,
    uiConfiguration: wcstUIConfiguration,
  },
  [TestCategory.BinocularVision]: {
    availableOptions: availableNPCTTestConfigurationOptions,
    defaultConfiguration: npctTestConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.NPCT_TEST_START,
      receivedMessage: MessageOpcode.NPCT_TEST_UPDATE,
    },
    availableProgressConfigurations: null,
    defaultProgressConfiguration: null,
    uiConfiguration: npctUIConfiguration,
  },
  [TestCategory.OptokineticNystagmus]: {
    availableOptions: TestSpecifics.availableOptokineticNystagmusTestConfigurationOptions,
    defaultConfiguration: TestSpecifics.optokineticNystagmusTestConfiguration,
    eventCodes: {
      startMessage: MessageOpcode.OKN_TEST_START,
      receivedMessage: MessageOpcode.OKN_TEST_UPDATE,
      nextMessage: MessageOpcode.OKN_TEST_NEXT,
    },
    availableProgressConfigurations: null,
    defaultProgressConfiguration: null,
    uiConfiguration: TestSpecifics.optokineticNystagmusUIConfiguration,
  },
};

export const TESTS_AWAITING_PATIENT_TRIGGER: TestCategory[] = [
  TestCategory.VisualField,
  TestCategory.FDT,
  TestCategory.ContrastSensitivity,
  TestCategory.Pupillometry,
  TestCategory.Dyslexia,
  TestCategory.ReadingAssessment,
  TestCategory.ContinuousPerformance,
  TestCategory.OcularMotility,
  TestCategory.Cover,
  TestCategory.Videonystagmography,
  TestCategory.Kinetic,
  TestCategory.BinocularVision,
  TestCategory.OptokineticNystagmus,
  TestCategory.StereoAcuity,
];
